import { memo, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  ButtonSelect,
  Icon,
  InputField,
  Link as DSLink,
  MenuMulti,
  type MenuMultiOption,
} from '@saviynt/design-system';
import { useIntl } from 'react-intl';

import { TOKENS } from '../../../../shared/constants';
import { capitalizeFirstLetter } from '../../../../shared/utils/tableUtils';
import { mockPolicyFilterData } from '../../data-sources/mock';

import './PolicyPageFilter.css';

export interface PolicyFilterValues {
  searchValue: string;
  selectedFilters: Record<string, string[]>;
}

interface PolicyPageFilterProps {
  onFilterChange?: (filters: PolicyFilterValues) => void;
}

interface PolicyFilter {
  label: string;
  value: string;
  options: MenuMultiOption[];
}

const buildFilterConfig = (): PolicyFilter[] =>
  Object.entries(mockPolicyFilterData).map(([key, { options }]) => ({
    label: capitalizeFirstLetter(key) ?? key,
    value: key,
    options: options.map(({ id, name }) => ({
      label: name,
      value: String(id),
      isChecked: false,
    })),
  }));

/**
 * Search and filter bar for the AI Agent Policies (Rules) page.
 *
 * Mirrors the Manage page filter: a debounced search input, a Filter
 * ButtonSelect with a dot Badge when any selection is active, and a
 * collapsible row of MenuMulti pills with staggered rise-in animation. A
 * single Clear link is rendered only when at least one filter is active.
 */
function PolicyPageFilter({ onFilterChange }: PolicyPageFilterProps) {
  const intl = useIntl();

  const [searchValue, setSearchValue] = useState('');
  const [isFilterBar, setIsFilterBar] = useState(false);
  const [filters, setFilters] = useState<PolicyFilter[]>(buildFilterConfig);

  const isBadgeIcon = useMemo(
    () => filters.some((f) => f.options.some((o) => o.isChecked)),
    [filters]
  );

  const emitChange = (
    nextFilters: PolicyFilter[],
    nextSearch: string = searchValue
  ) => {
    const selectedFilters = nextFilters.reduce<Record<string, string[]>>(
      (acc, f) => {
        const values = f.options
          .filter((o) => o.isChecked)
          .map((o) => String(o.value));
        if (values.length > 0) {
          acc[f.value] = values;
        }
        return acc;
      },
      {}
    );
    onFilterChange?.({ searchValue: nextSearch, selectedFilters });
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    emitChange(filters, value);
  };

  const handleFilterChange = (fk: string, so: MenuMultiOption[]) => {
    setFilters((prev) => {
      const next = prev.map((f) =>
        f.value === fk
          ? {
              ...f,
              options: f.options.map((o) => ({
                ...o,
                isChecked: so.some((_o) => _o.value === o.value),
              })),
            }
          : f
      );
      emitChange(next);
      return next;
    });
  };

  const handleFooterLink2Click = (fk: string) => {
    setFilters((prev) => {
      const next = prev.map((f) =>
        f.value === fk
          ? {
              ...f,
              options: f.options.map((o) => ({ ...o, isChecked: false })),
            }
          : f
      );
      emitChange(next);
      return next;
    });
  };

  const handleClearBtn = () => {
    setFilters((prev) => {
      const next = prev.map((f) => ({
        ...f,
        options: f.options.map((o) => ({ ...o, isChecked: false })),
      }));
      emitChange(next);
      return next;
    });
  };

  useEffect(() => {
    onFilterChange?.({ searchValue: '', selectedFilters: {} });
  }, []);

  return (
    <div className='PolicyPageFilter'>
      <div className='PolicyPageFilter-controls'>
        <div className='PolicyPageFilter-row'>
          <InputField
            id='policy-search-input'
            name='search'
            size='medium'
            placeholder={intl.formatMessage({
              id: 'YNc8i3',
              defaultMessage: 'Search by name or description',
              description: 'Search input placeholder',
            })}
            kind='search'
            value={searchValue}
            setValue={handleSearchChange}
            prefixIcon={
              <Icon kind='Search' size='small' color={TOKENS.CF_NEU_STL} />
            }
            classNames={{ container: 'PolicyPageFilter-search' }}
          />
          <ButtonSelect
            label={intl.formatMessage({
              id: 'vYViQu',
              defaultMessage: 'Filter',
              description: 'Filter',
            })}
            onClick={() => setIsFilterBar((prev) => !prev)}
            isOpen={isFilterBar}
            size='medium'
            prefixIcon={<Icon kind='Filter' />}
            BadgeComp={
              isBadgeIcon ? (
                <Badge kind='dot' color='primary' size='small' />
              ) : undefined
            }
            isVisualCueEnabled={false}
          />
        </div>
      </div>
      <div
        className='PolicyPageFilter-pillWrapper'
        data-open={isFilterBar}
        aria-hidden={!isFilterBar}
        {...({ inert: !isFilterBar ? '' : undefined } as Record<
          string,
          string | undefined
        >)}>
        <div className='PolicyPageFilter-pillRow'>
          {filters.map((f) => (
            <MenuMulti
              key={f.value}
              label={f.label}
              size='large'
              options={f.options}
              trigger={<ButtonSelect kind='pill' label='' />}
              onChange={(so) => {
                handleFilterChange(f.value, so ?? []);
              }}
              heightToShowScrollbar='16rem'
              minOptionsForFooter={6}
              minOptionsForSearch={0}
              footerLink1Text=' '
              footerLink2Text={intl.formatMessage({
                id: 'sOTYaP',
                defaultMessage: 'Clear Selection',
                description: 'Clear Selection',
              })}
              onFooterLink2Click={() => handleFooterLink2Click(f.value)}
              showOptionLabelOnHover
              isPortalEnabled
            />
          ))}
          {isBadgeIcon && (
            <DSLink
              text={intl.formatMessage({
                id: 'e9OvzW',
                defaultMessage: 'Clear',
                description: 'Clear',
              })}
              kind='button'
              onClick={handleClearBtn}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(PolicyPageFilter);
